import { useCallback } from 'react'

import { useLazyGetRouteStopsQuery } from 'api/route/getRouteStops'
import { useLazyGetRouteStopsUnassignedQuery } from 'api/route/getRouteStopsUnassigned'
import { UNASSIGNED_ROUTE } from 'containers/new-route-manager/settings'
import { useRouteManagerV3 } from 'hooks/useRouteManagerV3'

import type { RouteStopApi } from 'types/api/route/routeStop'

interface WorkOrderLocator {
  readonly workOrderId?: string | null
  readonly routeId?: string | null
  readonly serviceDate?: string | null
}

type Resolve = (locator: WorkOrderLocator) => Promise<string | null>

/**
 * Work-order reads don't carry `routeSlotId`, so read it off the route's stops (or the unassigned pool) at save,
 * where a fast click can't beat it. Null off the v3 gate or when not found, which leaves v3 falling back to v1.
 */
export const useResolveWorkOrderRouteSlotId = (): Resolve => {
  const isRouteManagerV3 = useRouteManagerV3()
  const [getRouteStops] = useLazyGetRouteStopsQuery()
  const [getUnassignedStops] = useLazyGetRouteStopsUnassignedQuery()

  return useCallback(
    async ({ workOrderId, routeId, serviceDate }) => {
      if (!isRouteManagerV3 || !workOrderId || !serviceDate) return null
      const isRouted = Boolean(routeId) && routeId !== UNASSIGNED_ROUTE.id
      const readStops = async (preferCacheValue: boolean): Promise<RouteStopApi[]> =>
        isRouted
          ? ((await getRouteStops({ routes: [routeId], serviceDate }, preferCacheValue).unwrap())?.routes ?? []).flatMap(
              (route: { stops?: RouteStopApi[] }) => route.stops ?? []
            )
          : (((await getUnassignedStops({ serviceDate }, preferCacheValue).unwrap())?.stops ?? []) as RouteStopApi[])
      const findSlot = (stops: RouteStopApi[]): string | null => stops.find(stop => stop.workOrderId === workOrderId)?.routeSlotId ?? null
      try {
        // A cached read can predate the stop landing there, so a miss earns one fresh read before giving up.
        return findSlot(await readStops(true)) ?? findSlot(await readStops(false))
      } catch {
        return null
      }
    },
    [isRouteManagerV3, getRouteStops, getUnassignedStops]
  )
}
